import type { NextPageContext } from 'next';
import type { ParsedUrlQueryInput } from 'querystring';
import { format, type UrlObject } from 'url';

import {
  BASE_CAREER_URL,
  BASE_HOW_TO_CHANGE_STRAP_URL,
  BASE_MALLELONDON_URL,
  BASE_PRESS_LOUNGE_URL,
  BASE_STORE_FINDER_URL,
  PLACEHOLDER_COUNTRY_SLUG,
  PLACEHOLDER_LANGUAGE_SLUG,
  PLACEHOLDER_SF_ID_SLUG,
} from '@/lib/const';
import { getLocaleFromPath } from '@/lib/locales/locales';

export const externalLinkReg = /^(http|https):\/\//;
export const parseUrlOrigin = (url: string) => {
  try {
    const urlObj = new URL(url);
    return urlObj.origin;
  } catch (e) {
    return url;
  }
};
export const getUrlText = (link: string) => {
  if (externalLinkReg.test(link)) return link;
  return link.startsWith('/') ? link : `/${link}`;
};

export const extractPathFromUrl = (url: string) => {
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.pathname;
  } catch (error) {
    // handle partial url
    const [path] = url.split('?');
    return path;
  }
};

export function removeNxtPQueryParams(url: string): string {
  const [path, queryString] = url.split('?');

  if (!queryString) {
    return path;
  }

  const searchParams = new URLSearchParams(queryString);

  const filteredParams = Array.from(searchParams.entries()).filter(
    ([key]) => !key.startsWith('nxtP'),
  );

  const remainingQueryString = new URLSearchParams(filteredParams).toString();

  return remainingQueryString ? `${path}?${remainingQueryString}` : path;
}

export function extractPageUrlFromNextContext(
  ctx: NextPageContext,
  domain: string,
) {
  let currentUrl = ctx.asPath
    ? `${removeTrailingSlash(domain)}${ctx.asPath}`
    : '';
  if (ctx.locale && !ctx.asPath?.includes(ctx.locale)) {
    currentUrl = ctx.asPath ? `${domain}${ctx.locale}${ctx.asPath}` : '';
  }
  return removeNxtPQueryParams(currentUrl);
}

export function removeTrailingSlash(uri: string): string {
  uri = uri.replace(/%2f$/gi, '');
  uri = uri.replace(/\/$/g, '');
  return uri;
}

export function addTrailingSlash(url: string): string {
  const [path, suffix = ''] = url.split(/([?#].*)/);

  if (path === '' || path === '/') {
    return url;
  }
  const normalizedPath = path.endsWith('/') ? path : path + '/';

  return normalizedPath + suffix;
}

export function isValidBreitlingDomain(src: string): boolean {
  try {
    const url = new URL(src);
    return (
      url.hostname.endsWith('.breitling.com') ||
      url.hostname === 'breitling.com'
    );
  } catch (error) {
    return false;
  }
}

export function injectCountrySlugAndLanguageSlugIntoUrl({
  countrySlug,
  languageSlug,
  salesForceId,
  url,
}: {
  countrySlug: string;
  languageSlug: string;
  salesForceId?: string;
  url: string;
}): string {
  return url
    .replaceAll(PLACEHOLDER_COUNTRY_SLUG, countrySlug)
    .replaceAll(PLACEHOLDER_LANGUAGE_SLUG, languageSlug)
    .replaceAll(PLACEHOLDER_SF_ID_SLUG, salesForceId ?? '');
}

const linkAriaLabelMap: {
  [key: string]: string;
} = {
  '/': 'Home',
  '/my-account/login/': 'Register your Breitling - My account',
  '/stores/': 'Find a boutique - Stores',
  '/service/warranties/': 'Discover International warranty',
  '/about/': 'Discover About',
  '/straps/packs/': 'Discover All the strap packs',
  '/campaigns/books/': 'Breitling Books',
  '/service/warranties/#warranty-extention/':
    'Warranty extension - Extend My Warranty',
  '/watches/men/': `Discover now - Men’s watches`,
  '/straps/packs/surfer-pack/': 'Discover Sufer pack',
  '/straps/packs/gentleman-pack/': 'Discover Gentleman pack',
  '/straps/packs/weekender-pack/': 'Discover Weekender pack',
  '/service/our-services/': 'Discover now - Our services',
  '/watches/novelties/': 'Discover now - Novelties',
  '/terms-of-use/':
    'Breitling terms of use and accepted the Terms and Conditions – Additional Services',
  '/straps/all/': 'All straps',
  '/privacy-policy/': 'Breitling Privacy Policy',
  '/straps/packs/explorer-pack/': 'Discover Explorer pack',
  '/service/watch-care/': 'Discover Watch care',
  '/about/digital-passport/': 'Discover Digital passport',
  '/watches/limited-editions/': 'Limited editions',
  '/watches/mens-watches/': `Discover now - Men’s watches`,
  '/terms-and-conditions/': 'Terms & conditions',
  '/about/partnerships/': 'Discover partnerships',
  '/140-years/': 'Discover 140 years',
  '/watches/women/': `Discover Women's watches`,
  '/about/icons/': 'Discover Our icons',
  '/collections/superocean-heritage/':
    'See more - Discover the collection - Superocean Heritage',
  '/collections/superocean/': 'See more - Discover the collection - Superocean',
  '/collections/top-time/': 'See more - Discover the collection - Top Time',
  '/collections/premier/': 'See more - Discover the collection - Premier',
  '/collections/navitimer/': 'See more - Discover the collection - Navitimer',
  '/collections/classic-avi/':
    'See more - Discover the collection - Classic Avi',
  '/collections/avenger/': 'See more - Discover the collection - Avenger',
  '/collections/chronomat/': 'See more - Discover the collection - Chronomat',
  '/watches/chronomat-nfl-team-editions/':
    'Explore Discover all NFL Team Editions',
  '/my-account/my-watches/': 'Register your Breitling - REGISTER YOUR WATCH',
  '/about/madebybreitling/': 'Discover Watchmaking',
  '/about/squads/': 'Discover Ambassadors',
  '/service/service-prices/': 'Discover Service prices',
  '/watches/gifting/': 'Discover Gifts',
  '/140-years/stories-of-breitling/': 'Discover our history',
  '/about/history/': 'Discover history',
  '/about/partnerships/chronomat-nfl-team-editions/':
    'Find your team - NFL Team Editions',
  '/service/my-watch-needs-a-service/':
    'My watch needs a service - REQUEST YOUR SERVICE',
  '/straps/change-watch-strap/': 'Learn how - How to change a strap',
  '/watches/all/': 'All watches - Discover',
  '/watches/collections/professional/':
    'Professional watches - Discover the collection - See more',
  '/collections/professional/':
    'Professional watches - Discover the collection - See more',
  '/about/sustainability/': 'SUSTAINABILITY - Discover',
  '/watches/': 'Watches - Discover Watches',
  '/straps/': 'Straps - Discover Straps',
  '/watches/collections/':
    'Discover the All Collections - Discover the Breitling Collections',
  '/service/': 'Discover Services - EXPLORE #BREITLINGCARE',
};

export const getLinkAriaLabel = (
  url: UrlObject | string,
  label?: string | null,
  fromUrl?: boolean,
) => {
  if (!url) return undefined;

  if (typeof url === 'string') {
    const formattedUrl = url.endsWith('/') ? url : url + '/';

    switch (formattedUrl) {
      case BASE_STORE_FINDER_URL:
        return 'Discover store';
      case BASE_HOW_TO_CHANGE_STRAP_URL:
        return 'Learn how to change the strap';
      case BASE_CAREER_URL:
        return 'Career';
      case BASE_PRESS_LOUNGE_URL:
        return 'Press Lounge';
      case BASE_MALLELONDON_URL:
        return 'MalleLondon - Discover the collection';
    }
  }

  const fullUrl =
    url && typeof url !== 'string'
      ? format({
          pathname: Object.entries(url.query || {}).reduce(
            (path, [key, value]) => path?.replace(`[${key}]`, value as string),
            url.pathname,
          ),
          query: {},
        })
      : url;

  const urlSplits = fullUrl.split('?');
  let newUrl = urlSplits[0];
  newUrl = newUrl.startsWith('http')
    ? newUrl.split('/').slice(4).join('/')
    : newUrl;
  newUrl = newUrl.endsWith('/') ? newUrl : newUrl + '/';
  newUrl = newUrl.startsWith('/') ? newUrl : '/' + newUrl;

  if (linkAriaLabelMap[newUrl.toLocaleLowerCase()])
    return linkAriaLabelMap[newUrl.toLocaleLowerCase()];
  if (label) return label;
  if (fromUrl) {
    const linkSplits = newUrl && newUrl.split('/');
    return `Discover ${linkSplits[1] ?? ''} ${linkSplits[2] ?? ''}`;
  }
  return undefined;
};

export const ensureLocale = (
  url: string | UrlObject,
  locale: string,
): string | UrlObject => {
  if (typeof url === 'string') {
    const trimmedUrl = url.trim();
    const isExternalLink =
      trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://');
    const isSpecialLink =
      trimmedUrl.startsWith('tel:') || trimmedUrl.startsWith('mailto:');

    const finalLocale = getLocaleFromPath(url) ?? locale; // if url already have locale, use url's locale
    const [countrySlug, localeSlug] = finalLocale.split('-');
    const urlWithCountryAndLanguage = injectCountrySlugAndLanguageSlugIntoUrl({
      url: trimmedUrl,
      countrySlug: countrySlug.toUpperCase(),
      languageSlug: localeSlug.toLowerCase(),
    });

    return urlWithCountryAndLanguage.startsWith(`/${finalLocale}`) ||
      isSpecialLink ||
      isExternalLink
      ? urlWithCountryAndLanguage
      : `/${finalLocale}${
          urlWithCountryAndLanguage.startsWith('/')
            ? urlWithCountryAndLanguage
            : `/${urlWithCountryAndLanguage}`
        }`;
  } else {
    const finalLocale = getLocaleFromPath(url.pathname ?? '') ?? locale; // if url already have locale, use url's locale
    return {
      ...url,
      pathname: url.pathname
        ? url.pathname.startsWith(`/${finalLocale}`)
          ? url.pathname
          : `/${finalLocale}${url.pathname}`
        : `/${finalLocale}`,
    };
  }
};

/**
 * Checks if pathname matches the target path (ignoring trailing slash differences)
 * @param pathname - The current pathname
 * @param targetPath - The target path to compare against
 * @returns True if paths match, false otherwise
 */
export function isPathMatch(
  pathname: string | null | undefined,
  targetPath: string | null | undefined,
): boolean {
  if (
    pathname === null ||
    pathname === undefined ||
    targetPath === null ||
    targetPath === undefined
  ) {
    return false;
  }
  return removeTrailingSlash(pathname) === removeTrailingSlash(targetPath);
}

function isValidPathParam(value: unknown): value is string | number | boolean {
  return ['string', 'number', 'boolean'].includes(typeof value);
}

// Replace param in pathname with the actual value eg: /us-en/sunglasses/[modelSku]/ -> /us-en/sunglasses/cloudbreak-0004-2-JZ310610.25/
export const buildResolvedPath = (
  pathname: string,
  query: string | ParsedUrlQueryInput | null | undefined,
): string => {
  if (!pathname) return '';

  let resolvedPath = pathname;
  const searchParams = new URLSearchParams();

  if (query && typeof query === 'object') {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null) continue;

      const paramPlaceholder = `[${key}]`;

      // Handle path parameter replacement
      if (resolvedPath.includes(paramPlaceholder) && isValidPathParam(value)) {
        resolvedPath = resolvedPath.replace(paramPlaceholder, String(value));
        continue;
      }

      // Handle query parameters
      if (Array.isArray(value)) {
        value.forEach((v) => v !== undefined && searchParams.append(key, v));
      } else {
        searchParams.append(key, value as string);
      }
    }

    const queryString = searchParams.toString();
    if (queryString) {
      resolvedPath += `?${queryString}`;
    }
  }

  return resolvedPath;
};

export function getIntParam(param: string): number {
  if (!param) return 0;

  try {
    const raw = decodeURIComponent(param); // handle %20, %3A, etc.
    const digits = raw.match(/\d+/)?.[0]; // extract first number sequence
    return digits ? parseInt(digits, 10) : 0;
  } catch {
    return 0;
  }
}
